import { zodResolver } from "@hookform/resolvers/zod";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateInterviewEta } from "../../api/useUpdateInterviewEta";
import { InterviewUpdateEtaBottomSheet } from "./InterviewUpdateEtaBottomSheet";
import { type InterviewUpdateEtaSource } from "./types";
import { formatInterviewEtaMessage } from "./utils/formatInterviewEtaMessage";
import { getInterviewEtaAvailableTimes } from "./utils/getInterviewEtaAvailableTimes";

const updateEtaSchema = z.object({
  etaAt: z.date(),
  additionalMessage: z.string(),
});

type UpdateEtaFormValues = z.infer<typeof updateEtaSchema>;

interface InterviewUpdateEtaContainerProps {
  modalState: UseModalState;
  interviewId: string;
  interviewStart: Date;
  workplaceId: string;
  workplaceTimezone: string;
  source: InterviewUpdateEtaSource;
  onEtaUpdated: () => void;
}

export function InterviewUpdateEtaContainer(props: InterviewUpdateEtaContainerProps) {
  const {
    modalState,
    interviewId,
    interviewStart,
    workplaceId,
    workplaceTimezone,
    source: sourceProperty,
    onEtaUpdated,
  } = props;

  const { source: sourceQueryParameter } = useSearchParams<{
    source?: InterviewUpdateEtaSource;
  }>();

  const source = sourceQueryParameter ?? sourceProperty;

  const { showSuccessToast, showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const workerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { mutateAsync: updateInterviewEta, isLoading: isSubmitting } = useUpdateInterviewEta();

  const etaAvailableTimes = getInterviewEtaAvailableTimes({
    interviewStart,
    now: new Date(),
  });
  // eslint-disable-next-line prefer-destructuring
  const initialEta = etaAvailableTimes[0];

  const { handleSubmit, setValue, watch } = useForm<UpdateEtaFormValues>({
    resolver: zodResolver(updateEtaSchema),
    defaultValues: {
      etaAt: initialEta,
      additionalMessage: "",
    },
  });

  const etaAt = watch("etaAt");
  const additionalMessage = watch("additionalMessage");
  const message = useMemo(() => {
    return formatInterviewEtaMessage({
      interviewStart,
      selectedEta: etaAt,
      workplaceTimezone,
      additionalMessage: additionalMessage || undefined,
    });
  }, [interviewStart, etaAt, workplaceTimezone, additionalMessage]);

  const onSubmit = handleSubmit(async (data: UpdateEtaFormValues) => {
    try {
      const messageToSend = formatInterviewEtaMessage({
        interviewStart,
        selectedEta: data.etaAt,
        workplaceTimezone,
        additionalMessage: data.additionalMessage ?? undefined,
      });

      await updateInterviewEta({
        interviewId,
        etaAt: data.etaAt,
        message: messageToSend,
      });

      logEvent(APP_V2_APP_EVENTS.UPDATE_INTERVIEW_ETA_SUBMITTED, {
        interviewId,
        workerId: worker.userId,
        workplaceId,
        interviewStart: interviewStart.toISOString(),
        etaSubmitted: data.etaAt.toISOString(),
        message: messageToSend,
        source,
      });

      showSuccessToast("Your ETA has been sent to the workplace.");
      onEtaUpdated();
      modalState.closeModal();
    } catch (error) {
      showErrorToast("Something went wrong while updating your ETA. Please try again.");
      logError(APP_V2_APP_EVENTS.UPDATE_INTERVIEW_ETA_FAILURE, {
        error,
        metadata: {
          interviewId,
          workplaceId,
        },
      });
    }
  });

  return (
    <InterviewUpdateEtaBottomSheet
      modalState={modalState}
      interviewStart={interviewStart}
      workplaceTimezone={workplaceTimezone}
      workerTimezone={workerTimezone}
      etaAvailableTimes={etaAvailableTimes}
      selectedEta={etaAt}
      message={message}
      additionalMessage={additionalMessage}
      isSubmitting={isSubmitting}
      onEtaChange={(eta: Date) => {
        setValue("etaAt", eta);
      }}
      onMessageChange={(newMessage) => {
        setValue("additionalMessage", newMessage);
      }}
      onSubmit={onSubmit}
    />
  );
}
